import React, { useState } from "react";
import styled, { css, keyframes, RuleSet } from "styled-components";
import {
  CheckOutlined,
  LinkOutlined,
  PushpinOutlined,
} from "@ant-design/icons";

import { Card } from "../../../../components/containers/styledComponents";
import {
  compassBordeaux,
  compassLightGreen,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
  lightGreys,
} from "../../../../styles";
import { SinV2 } from "../../../../types/engagementAssessmentV2Types";

import { EvidenceChips, FlagGlyph, FlagStack } from "./assessmentAtoms";
import { ConfidenceRing } from "./ScoreRing";
import { SinRow } from "./assessmentViewModel";

const CYCLE_MS = 5000;
const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const FLAG_TINT_SOFT = "rgba(221, 17, 85, 0.05)";
const FLAG_TINT_STRONG = "rgba(221, 17, 85, 0.09)";
const FLAG_HAIRLINE = "rgba(221, 17, 85, 0.16)";

const popIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.96); }
  to { opacity: 1; transform: none; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const fill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  80%, 100% { transform: scale(2.4); opacity: 0; }
`;

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 48px;
  padding: 8px 16px;
  box-sizing: border-box;
`;

const Title = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  font-family: "ApercuMedium";
  font-size: 13px;
  color: ${darkGreen};
`;

const TitleIcon = styled.span<{ $flagged: boolean }>`
  display: inline-flex;
  color: ${({ $flagged }): string =>
    $flagged ? compassBordeaux : compassLightGreen};
`;

const Rule = styled.span`
  width: 1px;
  height: 18px;
  background: ${lightGreyOutline};
  flex-shrink: 0;
`;

const Rail = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const Pill = styled.button<{
  $flagged: boolean;
  $active: boolean;
  $index: number;
}>`
  all: unset;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px 0 9px;
  border-radius: 999px;
  box-sizing: border-box;
  font-family: "Apercu";
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ $flagged, $active }): string =>
    $flagged ? compassBordeaux : $active ? darkGreen : greyText};
  background: ${({ $flagged, $active }): string =>
    $flagged ? ($active ? FLAG_TINT_STRONG : FLAG_TINT_SOFT) : "white"};
  box-shadow: inset 0 0 0 1px
    ${({ $flagged, $active }): string =>
      $flagged ? FLAG_HAIRLINE : $active ? lightGreyText : lightGreyOutline};
  transition:
    transform 0.18s ${EASE},
    box-shadow 0.18s ease,
    background 0.18s ease,
    color 0.18s ease;
  animation: ${popIn} 0.3s ${EASE} both;
  animation-delay: ${({ $index }): number => 60 + $index * 35}ms;
  ${reducedMotion}

  &:hover {
    transform: translateY(-1px);
  }
  &:focus-visible {
    box-shadow: inset 0 0 0 1px ${compassLightGreen};
  }

  > svg,
  > .anticon {
    flex-shrink: 0;
    transition: transform 0.22s ${EASE};
  }
  > .anticon {
    font-size: 9px;
    color: ${compassLightGreen};
  }
  &:hover > svg,
  &:hover > .anticon {
    transform: rotate(-8deg) scale(1.1);
  }
`;

const Beacon = styled.span`
  position: relative;
  display: inline-flex;
  width: 5px;
  height: 5px;
  margin-left: 1px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${compassBordeaux};
  }
  &::after {
    animation: ${ping} 1.6s ${EASE} infinite;
    ${reducedMotion}
  }
`;

const Status = styled.span<{ $flagged: boolean }>`
  flex-shrink: 0;
  font-family: "Apercu";
  font-size: 12px;
  color: ${({ $flagged }): string => ($flagged ? compassBordeaux : greyText)};
  white-space: nowrap;
`;

const CaptionShell = styled.div<{ $open: boolean }>`
  display: grid;
  /* zero intrinsic width: the pill line alone sizes the card */
  width: 0;
  min-width: 100%;
  grid-template-rows: ${({ $open }): string => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.28s ${EASE};
  ${reducedMotion}

  > div {
    overflow: hidden;
    min-height: 0;
  }
`;

const Caption = styled.div<{ $flagged: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 9px 16px 11px;
  border-top: 1px solid ${lightGreys.borderLightGrey};
  background: ${({ $flagged }): string =>
    $flagged ? "rgba(221, 17, 85, 0.025)" : "transparent"};
  border-radius: 0 0 10px 10px;
`;

const CaptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  animation: ${slideUp} 0.26s ${EASE} both;
  ${reducedMotion}
`;

const CaptionIcon = styled.span<{ $flagged: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  width: 11px;
  font-size: 11px;
  justify-content: center;
  color: ${({ $flagged }): string =>
    $flagged ? compassBordeaux : compassLightGreen};
`;

const CaptionLabel = styled.span`
  flex-shrink: 0;
  font-family: "ApercuMedium";
  font-size: 12.5px;
  color: ${darkGreen};
`;

const CaptionText = styled.span<{ $expanded: boolean }>`
  flex: 1;
  min-width: 0;
  font-family: "Apercu";
  font-size: 12.5px;
  line-height: 1.5;
  color: ${greyText};
  ${({ $expanded }): RuleSet<object> =>
    $expanded
      ? css`
          white-space: normal;
        `
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${greyText};

  .anticon {
    font-size: 10px;
  }
`;

const Evidence = styled.div`
  padding-left: 21px;
  animation: ${slideUp} 0.26s ${EASE} 0.05s both;
  ${reducedMotion}
`;

const Progress = styled.span<{ $paused: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1.5px;
  background: ${compassBordeaux};
  opacity: 0.3;
  transform-origin: left;
  animation: ${fill} ${CYCLE_MS}ms linear both;
  animation-play-state: ${({ $paused }): string =>
    $paused ? "paused" : "running"};

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const RedFlagsPanel = ({
  flagged,
  clean,
  isLoading,
}: RedFlagsPanelProps): JSX.Element => {
  const [hovered, setHovered] = useState<SinV2 | null>(null);
  const [pinned, setPinned] = useState<SinV2 | null>(null);
  const [cycle, setCycle] = useState<number>(0);

  const criteria = [...flagged, ...clean].sort((a, b) => a.index - b.index);
  const total = criteria.length;
  const hasFlags = flagged.length > 0;

  const find = (sin: SinV2 | null): SinRow | undefined =>
    sin === null ? undefined : criteria.find((c) => c.sin === sin);

  const cycled = hasFlags ? flagged[cycle % flagged.length] : undefined;
  const active = find(hovered) ?? find(pinned) ?? cycled ?? null;
  const expanded = active !== null && pinned === active.sin;
  const autoCycling = flagged.length > 1 && pinned === null;

  const onPillClick = (row: SinRow): void => {
    if (!row.isFlagged) return;
    setPinned((current) => (current === row.sin ? null : row.sin));
  };

  return (
    <Card
      isLoading={isLoading}
      w={100}
      wpx="fit-content"
      h="auto"
      m="0 20px 0 20px"
      p="0"
    >
      <Line>
        <Title>
          <TitleIcon $flagged={hasFlags}>
            {hasFlags ? <FlagGlyph size={12} /> : <CheckOutlined />}
          </TitleIcon>
          Red Flags
          <FlagStack flags={criteria.map((c) => c.isFlagged)} />
        </Title>
        <Rule />
        <Rail role="list" onMouseLeave={(): void => setHovered(null)}>
          {criteria.map((row, i) => (
            <Pill
              key={row.sin}
              type="button"
              role="listitem"
              $flagged={row.isFlagged}
              $active={active?.sin === row.sin}
              $index={i}
              aria-pressed={row.isFlagged ? pinned === row.sin : undefined}
              onMouseEnter={(): void => setHovered(row.sin)}
              onFocus={(): void => setHovered(row.sin)}
              onBlur={(): void => setHovered(null)}
              onClick={(): void => onPillClick(row)}
            >
              {row.isFlagged ? <FlagGlyph size={10} /> : <CheckOutlined />}
              {row.label}
              {row.isFlagged && <Beacon aria-hidden="true" />}
            </Pill>
          ))}
        </Rail>
        <Status $flagged={hasFlags}>
          {total === 0
            ? "Not evaluated"
            : hasFlags
              ? `${flagged.length} to review`
              : "All clear"}
        </Status>
      </Line>

      <CaptionShell $open={active !== null} aria-live="polite">
        <div>
          {active && (
            <Caption $flagged={active.isFlagged}>
              <CaptionRow key={`${active.sin}-${expanded}`}>
                <CaptionIcon $flagged={active.isFlagged}>
                  {expanded ? (
                    <PushpinOutlined />
                  ) : active.isFlagged ? (
                    <FlagGlyph />
                  ) : (
                    <CheckOutlined />
                  )}
                </CaptionIcon>
                <CaptionLabel>{active.label}</CaptionLabel>
                <CaptionText $expanded={expanded}>{active.judgement}</CaptionText>
                {active.evidenceRefs.length > 0 && (
                  <Meta>
                    <LinkOutlined />
                    {active.evidenceRefs.length}
                  </Meta>
                )}
                {active.confidence && (
                  <ConfidenceRing confidence={active.confidence} size="xs" />
                )}
                {active.isFlagged && flagged.length > 1 && (
                  <Meta>
                    {flagged.findIndex((f) => f.sin === active.sin) + 1}/
                    {flagged.length}
                  </Meta>
                )}
              </CaptionRow>
              {expanded && active.evidenceRefs.length > 0 && (
                <Evidence>
                  <EvidenceChips refs={active.evidenceRefs} max={8} />
                </Evidence>
              )}
              {autoCycling && (
                <Progress
                  key={cycle}
                  $paused={hovered !== null}
                  onAnimationEnd={(): void => setCycle((c) => c + 1)}
                />
              )}
            </Caption>
          )}
        </div>
      </CaptionShell>
    </Card>
  );
};

type RedFlagsPanelProps = {
  flagged: SinRow[];
  clean: SinRow[];
  isLoading: boolean;
};
